import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { catchError, filter, skip, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, ParamMap, Params, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subject, Subscription } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { SystemService } from '../../../../system/system.service';
import { DataType } from '../../../data-type-widgets/data-type/data-type-component';
import {
  ConditionalRowDetailsDTO,
  FieldConfigDTO,
  FormColumnConfigDTO,
  MaintainTabDTO,
  MenuDTO,
  MenuType,
} from '../../../../system/system-dtos';
import { EntityDetailsMode } from '../../maintain/maintain-form/maintain-form-mode.enum';
import { MaintainService } from '../../maintain/maintain.service';
import { EnvironmentService } from '../../../../common/services/env-configuration/environment.service';
import { EntityGroupDetailsService } from './entity-group-details.service';
import { MyErrorStateMatcher } from '../../../../common/field-validation/my-error-state-matcher';
import { DataSource } from '../../maintain/data-source/data-source';
import { MaintainTableComponent } from '../../maintain/maintain-table/maintain-table.component';
import { LmTitleService } from '../../../../common/services/title.service';
import { DataSourceFactory } from '../../maintain/data-source/data-source-factory';
import { AdminContextService } from '../../../../common/services/admin.context.service';
import {
  WAGERING_PROFILES_ENTITY_CODE,
  WAGERING_PROFILES_OVERALL_GROUP,
} from '../../../../feature/manage/offers/wagering-profiles/wagering-profile.constants';
import Isotope from 'isotope-layout';
import 'isotope-packery';

declare let $: any;

@Component({
  selector: 'app-entity-group-details',
  templateUrl: './entity-group-details.component.html',
  styleUrls: ['./entity-group-details.component.scss'],
  standalone: false,
})
export class EntityGroupDetailsComponent implements OnInit, OnDestroy {
  @Input() includeHeader = true;

  @Input() entityCode?: string;

  @ViewChild('grid', { static: false }) gridRef!: ElementRef;

  menuType = MenuType;

  readonly EntityDetailsMode = EntityDetailsMode;

  contentLoading = true;

  code: string;

  routeBase: string;

  id: string;

  group: string;

  entityGroupId: string;

  menuConfig: MenuDTO;

  groupConfig: MenuDTO;

  data: any;

  mode: EntityDetailsMode;

  dataSourceMode: number;

  formGroup: UntypedFormGroup;

  error: HttpErrorResponse;

  validationErrors: any[];

  dataSource: DataSource;

  conditional: ConditionalRowDetailsDTO;

  columns: FormColumnConfigDTO[];

  isTableView = false;

  availableColumns: FormColumnConfigDTO[];

  public initialIndex = 0;

  public headerValue: any;

  private routeConfData: any;

  private cohortSubscription: Subscription;

  private currentCohortId: number;

  refreshSubject: Subject<void> = new Subject<void>();

  private isotopeInstance!: Isotope;

  constructor(
    private systemService: SystemService,
    public route: ActivatedRoute,
    private maintainService: MaintainService,
    private envService: EnvironmentService,
    private router: Router,
    private entityGroupService: EntityGroupDetailsService,
    private toastrService: ToastrService,
    private dataSourceFactory: DataSourceFactory,
    private lmTitleService: LmTitleService,
    private adminContextService: AdminContextService
  ) {
    this.cohortSubscription = this.adminContextService
      .getCohortWatcher()
      .pipe(filter((e) => !!e))
      .subscribe((e) => {
        if (this.currentCohortId && this.currentCohortId !== e.id) {
          this.refreshPage();
        }
        this.currentCohortId = e.id;
      });
  }

  public static getFormControlForField(
    f: FieldConfigDTO,
    responseData: any,
    c: FormColumnConfigDTO,
    mode: EntityDetailsMode
  ) {
    let value = null;
    let data = null;

    if (!this.isEmpty(responseData) && responseData[c.code]) {
      value = responseData[c.code][f.name];
      data = responseData[c.code];
    }

    const editReadOnly = f.editReadOnly && eval(f.editReadOnly);
    const disabled =
      ((mode === EntityDetailsMode.EDIT || mode === EntityDetailsMode.VIEW) && editReadOnly) ||
      (mode === EntityDetailsMode.ADD && f.addReadOnly);

    const validators = MyErrorStateMatcher.convertToAngularValidators([f]);
    const formControl = new UntypedFormControl(
      {
        value,
        disabled,
      },
      validators
    );
    return formControl;
  }

  public static isEmpty(data: any) {
    return !data || Object.keys(data).length === 0;
  }

  ngOnInit() {
    this.entityGroupService.reloadSubject = new BehaviorSubject<ParamMap>(null);

    this.route.parent.paramMap.subscribe((d) => this.entityGroupService.reloadSubject.next(d));
    this.route.paramMap.subscribe((d) => this.entityGroupService.reloadSubject.next(d));
    this.entityGroupService.reloadSubject
      .pipe(
        tap((params) => {
          this.contentLoading = true;
          this.code = params.get('entityCode') ?? this.route.snapshot.data['entityCodeOverride'] ?? this.entityCode;
          this.routeBase =
            params.get('entityCode') ?? this.route.snapshot.routeConfig?.path?.split('/')[0] ?? this.code;
          this.id = params.get('entityId') ?? this.route.parent.snapshot.paramMap.get('entityId');
          this.group = params.get('groupId') ?? this.group;
          this.entityGroupId = params.get('groupEntityId');
          const dataSourceMode = this.route.snapshot.queryParams._mode;
          this.dataSourceMode = dataSourceMode || 0;
        }),
        switchMap(() => this.route.data),
        tap((data) => {
          this.routeConfData = data;
        }),
        switchMap(() => this.systemService.getMenuConfig(this.code)),
        tap((conf: MenuDTO) => {
          this.mode =
            conf.entityGroup && conf.entityGroup.entryForm ? EntityDetailsMode.REPORT : this.routeConfData.mode;
          if (!conf) {
            this.router.navigate(['']);
          }
          this.doInitActionsBasedOnMenuConfig(conf);
        }),
        switchMap(() => this.getDataAndHandle()),
        catchError((e) => EMPTY)
      )
      .subscribe(
        (d) => {
          this.initializeIsotope();
        },
        (e) => console.log('failed', e)
      );

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.determineIndex();
      }
    });

    this.entityGroupService.isotopeLayout.subscribe((_) => this.layoutEvent());
  }

  private doInitActionsBasedOnMenuConfig(conf: MenuDTO) {
    this.menuConfig = conf;
    this.groupConfig = this.getGroupConfig(conf);
    this.dataSource = this.dataSourceFactory.get(this.groupConfig.menuType);
    this.determineIsTableView();
    this.determineIndex();
    this.determineTabTitle();
  }

  determineIndex() {
    this.initialIndex = this.menuConfig?.entityGroup.menuItems.findIndex(
      (item) => item.code === this.route.snapshot.params.groupId
    );
  }

  private getDataAndHandle() {
    if (this.isTableView) {
      this.contentLoading = false;
      return EMPTY;
    }
    return this.getOne().pipe(
      tap((data) => {
        this.data = data;
        this.createFormGroupsIfNeeded(data);
        this.availableColumns = this.getAvailableColumns();
      }),
      catchError((error) => {
        if (this.isItGeneratedReportView(this.route.snapshot.queryParams)) {
          this.goBackToReportParamsForm();
        } else if (error.status === 400 || error.status === 404) {
          this.router.navigateByUrl(`/${this.routeBase}`, { replaceUrl: true });
        }
        this.contentLoading = false;
        return EMPTY;
      }),
      tap(() => {
        this.contentLoading = false;
      })
    );
  }

  private getGroupConfig(conf: MenuDTO): MenuDTO {
    const groupConf = this.getGroupConf(conf);
    if (!groupConf) {
      // gbw
      // const defaultTab = conf.entityGroup.menuItems[0];
      const defaultTab =
        conf.entityGroup.menuItems.length > 0 ? conf.entityGroup.menuItems[0] : conf.entityGroup.entryForm;
      this.group = defaultTab.code;
      this.router.navigateByUrl(`/${this.routeBase}/${this.id}/${defaultTab.code}`, { replaceUrl: true });
      return defaultTab;
    }
    return groupConf;
  }

  private getGroupConf(conf: MenuDTO) {
    if (this.mode === EntityDetailsMode.ADD) {
      return conf.entityGroup.createConfig;
    }
    if (this.mode === EntityDetailsMode.REPORT && !this.route.snapshot.queryParams.show) {
      return conf.entityGroup.entryForm;
    }

    return conf.entityGroup.menuItems.filter((i) => i.code === this.group)[0];
  }

  private getOne(): Observable<any> {
    const { queryParams } = this.route.snapshot;
    const tabs = this.dataSource.getTabs(this.groupConfig);
    const tab = tabs ? tabs[0] : undefined;

    const rawUrl = this.getUrl(tab, queryParams);
    if (!rawUrl) {
      return of({});
    }

    if (this.isItEntryFormForReport(queryParams) && queryParams.rbody) {
      return of(JSON.parse(queryParams.rbody));
    }
    const url = rawUrl.replace(/{.+?}/, this.id).replace(/{.+?}/, this.entityGroupId);

    const method = this.isItGeneratedReportView(queryParams) ? 'POST' : 'GET';
    const body = this.isItGeneratedReportView(queryParams) ? JSON.parse(queryParams.rbody) : {};

    return this.maintainService.selectOne(url, null, body, method).pipe(
      switchMap((baseEntity) => {
        if (tab && tab.tableView.conditionals && tab.tableView.conditionals.length > 0) {
          this.conditional = MaintainTableComponent.getConditionalByTab(baseEntity, tab);
          const nestedUrl = this.constructNestedUrl(baseEntity, this.conditional);
          return this.maintainService.selectOne(nestedUrl);
        }
        return of(baseEntity);
      })
    );
  }

  private constructNestedUrl(element: any, conditional: ConditionalRowDetailsDTO) {
    let { url } = conditional;
    Object.keys(element).forEach((k) => {
      url = url.replace(`{${k}}`, element[k]);
    });
    return url;
  }

  private getUrl(tab: MaintainTabDTO, queryParams: Params): string {
    if (this.isItGeneratedReportView(queryParams)) {
      return this.menuConfig.entityGroup.selectUrl;
    }
    if (this.mode === EntityDetailsMode.REPORT && queryParams.show) {
      return null;
    }
    if (this.mode === EntityDetailsMode.REPORT) {
      return this.groupConfig.selectUrl;
    }
    if (this.mode === EntityDetailsMode.ADD) {
      return this.groupConfig.selectUrl;
    }
    if (tab && this.entityGroupId) {
      return tab.endpoints.selectOneUrl;
    }
    return this.groupConfig.selectUrl ? this.groupConfig.selectUrl : this.menuConfig.entityGroup.selectUrl;
  }

  private isItGeneratedReportView(queryParams) {
    return this.mode === EntityDetailsMode.REPORT && queryParams.show;
  }

  private isItEntryFormForReport(queryParams) {
    return this.mode === EntityDetailsMode.REPORT && !queryParams.show;
  }

  private createFormGroupsIfNeeded(data: any) {
    this.columns = this.getAllColumns();

    const parentFormGroupBuilder = {};
    this.columns
      .filter((c) => !!c.code)
      .forEach((c) => {
        if (c.nested) {
          const formGroupBuilder = {};
          for (const field of c.fields) {
            if (field.type === DataType.promo_claim_rate) {
              continue;
            }
            formGroupBuilder[field.name] = EntityGroupDetailsComponent.getFormControlForField(
              field,
              data,
              c,
              this.mode
            );
          }
          parentFormGroupBuilder[c.code] = new UntypedFormGroup(formGroupBuilder);
        } else {
          const field = c.fields[0];
          if (field !== undefined) {
            parentFormGroupBuilder[field.name] = EntityGroupDetailsComponent.getFormControlForField(
              field,
              data,
              c,
              this.mode
            );
          }
        }
      });
    this.formGroup = new UntypedFormGroup(parentFormGroupBuilder);
  }

  navigateToTable() {
    this.router.navigate([`/${this.routeBase}`]);
  }

  isEdit() {
    const { queryParams } = this.route.snapshot;
    return (
      [EntityDetailsMode.EDIT, EntityDetailsMode.ADD].includes(this.mode) || this.isItEntryFormForReport(queryParams)
    );
  }

  /** Dedicated Material overall UI — skip generic nested-table / subset stack. */
  isWageringProfileOverall(): boolean {
    return (
      this.code === WAGERING_PROFILES_ENTITY_CODE &&
      (this.group === WAGERING_PROFILES_OVERALL_GROUP || this.mode === EntityDetailsMode.ADD)
    );
  }

  onLoadingEmitter(loading: boolean) {
    if (!loading) {
      this.contentLoading = true;
      this.data = undefined;
      this.getDataAndHandle()
        .pipe(
          take(1),
          tap((_) => this.initializeIsotope())
        )
        .subscribe();
      this.refreshSubject.next();
    }
  }

  getAvailableColumns() {
    return this.columns.filter(
      (c: FormColumnConfigDTO) => !(c.ignorePanel && this.mode === EntityDetailsMode.VIEW && !this.data[c.code])
    );
  }

  getAllColumns() {
    if (this.conditional) {
      return this.conditional.detailsConfig.columns;
    }
    return this.groupConfig.singleForm.rows.map((r) => r.columns).reduce((acc, elem) => acc.concat(elem), []);
  }

  private determineIsTableView() {
    this.isTableView =
      this.groupConfig &&
      [MenuType.ELASTIC, MenuType.CASSANDRA, MenuType.MAINTAIN].includes(this.groupConfig.menuType) &&
      !this.entityGroupId;
  }

  getTitleOfNestedEntity() {
    let label = this.groupConfig.label;
    if (label.endsWith('s')) {
      label = label.substring(0, label.length - 1);
    }

    return `${label} #${this.entityGroupId}`;
  }

  navigateBackToList() {
    this.router.navigate([`./${this.routeBase}/${this.id}/${this.group}`], { relativeTo: this.route.parent });
  }

  headerInitized($event: any) {
    this.headerValue = $event;
    const { titleTag } = this.menuConfig.entityGroup;
    if (titleTag) {
      const header = this.headerValue || {};
      const titleSuffix = eval(titleTag);
      this.lmTitleService.setTitleSuffix(titleSuffix);
    }
  }

  private determineTabTitle() {
    let titleSuffix = '';
    if (this.groupConfig && this.entityGroupId) {
      titleSuffix = `${this.groupConfig.label} / ${this.entityGroupId}`;
    } else {
      const { titleTag } = this.menuConfig.entityGroup;
      if (!titleTag) {
        if (this.id === 'single') {
          titleSuffix = this.menuConfig.label;
        } else {
          titleSuffix = `${this.menuConfig.label} / ${this.id}`;
        }
      } else if (titleTag && this.headerValue) {
        const header = this.headerValue;
        titleSuffix = eval(titleTag);
      }
    }
    if (this.includeHeader) {
      this.lmTitleService.setTitleSuffix(titleSuffix);
    }
  }

  refreshPage() {
    this.ngOnInit();
    this.refreshSubject.next();
  }

  submitFull() {
    if (this.formGroup.invalid) {
      this.formGroup.markAsTouched();

      Object.keys(this.formGroup.controls).forEach((key: string) => {
        const formCOntrol = this.formGroup.get(key);
        formCOntrol.markAsTouched();
      });

      window.scroll(0, 0);

      this.toastrService.warning(
        `Check form once again. You forgot about some required fields: ${this.findInvalidControls()}`
      );
      return;
    }

    if (this.menuConfig.entityGroup.createConfig.singleForm.multipartForm === true) {
      this.submitMultipartForm();
    } else {
      this.submitNormalForm();
    }
  }

  public findInvalidControls(): string[] {
    const invalid = [];
    const { controls } = this.formGroup.controls[Object.keys(this.formGroup.controls)[0]] as UntypedFormGroup;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  // findInvalidControls(_input: AbstractControl, _invalidControls: AbstractControl[]): AbstractControl[] {
  //     if (!_invalidControls) {
  //         _invalidControls = [];
  //     }
  //     if (_input instanceof FormControl) {
  //         if (_input.invalid) {
  //             _invalidControls.push(_input);
  //         }
  //         return _invalidControls;
  //     }

  //     if (!(_input instanceof FormArray) && !(_input instanceof FormGroup)) {
  //         return _invalidControls;
  //     }
  //     const controls = _input.controls;
  //     for (const name in controls) {
  //         if (controls[name]) {
  //             const control = controls[name];
  //             if (control.invalid) {
  //                 _invalidControls.push(control);
  //             }
  //             switch (control.constructor.name) {
  //                 case 'FormArray':
  //                     (<FormArray>control).controls.forEach(_control => {
  //                         _invalidControls = this.findInvalidControls(_control, _invalidControls);
  //                     });
  //                     break;

  //                 case 'FormGroup':
  //                     _invalidControls = this.findInvalidControls(control, _invalidControls);
  //                     break;
  //             }
  //         }
  //     }

  //     return _invalidControls;
  // }

  public submitNormalForm() {
    if (EntityDetailsMode.REPORT === this.mode) {
      of(
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            cohortId: this.route.snapshot.queryParams.cohortId,
            rbody: JSON.stringify(this.formGroup.getRawValue()),
            show: true,
          },
        })
      )
        .pipe(
          switchMap(() => this.route.queryParams),
          skip(1),
          take(1)
        )
        .subscribe(
          (c) => {
            this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
          }
          //    this.ngOnInit()
        );
    } else if (EntityDetailsMode.ADD === this.mode) {
      this.entityGroupService
        .performHttpPost(
          this.menuConfig.entityGroup.createConfig.singleForm.updateUrl.url,
          this.formGroup.getRawValue()
        )
        .pipe(
          catchError((error) => {
            this.error = error;
            this.validationErrors = error.error.errors;
            window.scrollTo(0, 0);
            return EMPTY;
          })
        )
        .subscribe((d) => {
          this.router.navigate([`./${this.routeBase}/${(d.id ? d : d.overall ? d.overall : d.details).id}/overall`], {
            relativeTo: this.route.parent,
          });
          this.toastrService.success(`id ${(d.id ? d : d.overall ? d.overall : d.details).id} created.`);
        });
    }
  }

  private submitMultipartForm() {
    this.entityGroupService
      .submitMultipartForm(
        (this.formGroup.controls[Object.keys(this.formGroup.controls)[0]] as any).getRawValue(),
        this.menuConfig.entityGroup.createConfig.singleForm.updateUrl.url,
        this.formGroup,
        'POST'
      )
      .pipe(
        catchError((error) => {
          this.error = error;
          this.validationErrors = error.error.errors;
          window.scrollTo(0, 0);
          return EMPTY;
        })
      )
      .subscribe((d) => {
        this.router.navigate([`./${this.routeBase}/${(d.overall ? d.overall : d.details).id}/overall`], {
          relativeTo: this.route.parent,
        });
        this.toastrService.success(`File upload with id ${(d.overall ? d.overall : d.details).id} created.`);
      });
  }

  goBackToReportParamsForm() {
    of(
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          ...this.route.snapshot.queryParams,
          show: null,
        },
      })
    )
      .pipe(
        switchMap(() => this.route.queryParams),
        skip(1),
        take(1)
      )
      .subscribe(() => {
        this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
      });
  }

  ngOnDestroy(): void {
    this.entityGroupService.reloadSubject.complete();
    this.cohortSubscription.unsubscribe();
  }

  refreshReport() {
    this.entityGroupService.reloadSubject.next(this.route.snapshot.paramMap);
  }

  private initializeIsotope() {
    setTimeout(() => {
      if (this.isTableView || !this.gridRef?.nativeElement) {
        return;
      }

      const isotopeConfig = {
        percentPosition: true,
        itemSelector: '.grid-item',
        layoutMode: 'packery',
        packery: {
          gutter: 20,
        },
      };

      this.isotopeInstance = new Isotope(this.gridRef.nativeElement, isotopeConfig);
    }, 100);
  }

  layoutEvent() {
    setTimeout(() => {
      if (this.isotopeInstance) {
        this.isotopeInstance.layout();
      }
    }, 200);

    setTimeout(() => {
      if (this.isotopeInstance) {
        this.isotopeInstance.layout();
      }
    }, 800);
  }
}

export interface FileWrapper {
  fieldName: string;
  file: File;
}
